import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import RenderingErrorBoundary from 'components/RenderingErrorBoundary';
import FilesTab from 'components/shared/ai/summary/FilesTab';
import SummaryTab from 'components/shared/ai/summary/SummaryTab';
import CloseButton from 'components/shared/CloseButton';
import Foldout from 'components/shared/Foldout';
import Tabs, { TabType } from 'components/Tabs';
import { useReferral } from 'context/review/referral';
import Referral from 'models/reviews/Referral';
import { colors } from 'styles/theme/colors';
import { H3, Label } from 'styles/typography';
import ArrowIcon from 'svg/ArrowIcon';

export type TabTypeValue = 'files' | 'summary' | 'comparison';

type Props = {
  isNested?: boolean;
  open?: boolean;
  onClose?: (referral?: Referral) => void;
  initialTabValue?: TabTypeValue;
};

export default function Slideout({ isNested = false, open = true, onClose, initialTabValue }: Props) {
  const { referral } = useReferral();

  const { summary } = referral ?? {};
  const isSummarized = !!summary;

  const tabs: TabType[] = useMemo(
    () => [
      {
        label: 'Summary',
        value: 'summary',
      },
      {
        label: referral?.attachments?.length === undefined ? 'Files' : `Files (${referral?.attachments?.length})`,
        value: 'files',
      },
    ],
    [referral?.attachments?.length]
  );

  const [selectedTab, setSelectedTab] = useState<TabType>(() => {
    const initialTab = initialTabValue && tabs.find((tab) => tab.value === initialTabValue);

    return initialTab || tabs[0];
  });

  useEffect(() => {
    if (initialTabValue && open) {
      const initialTab = tabs.find((tab) => tab.value === initialTabValue);
      if (initialTab && selectedTab.value !== initialTabValue) {
        setSelectedTab(initialTab);
      }
    }
    // we deliberately don't want to re-run this effect when the selectedTab changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTabValue, open]);

  const handleTabClick = (tab: TabType) => {
    setSelectedTab(tab);
  };

  const pendingActions = useRef<Array<'summarize'>>([]);

  const handleSwitchToTab = (tabValue: TabTypeValue) => {
    const tab = tabs.find((tab) => tab.value === tabValue);
    if (tab) {
      setSelectedTab(tab);
    }
  };

  const handleSummarizeFromFilesTab = () => {
    pendingActions.current.push('summarize');
    handleSwitchToTab('summary');
  };

  const closeModal = () => {
    onClose?.(undefined);
  };

  return (
    <Foldout isNested open={open} width={`min(100%, ${isNested ? '1000' : '1200'}px)`} onBackdropClick={closeModal}>
      <Container>
        <PositionedClose onClick={closeModal} />
        <FilterFoldoutHeader>
          <Header>
            <StyledArrowIcon onClick={closeModal} />
            <TitleGroup>
              <H3>Referral Summarization</H3>
              <Label>Patient: {referral?.episode.patient.name ?? ''}</Label>
            </TitleGroup>
          </Header>
        </FilterFoldoutHeader>
        <TabsContainer>
          <Tabs tabs={tabs} onTabClick={handleTabClick} selectedTab={selectedTab} />
        </TabsContainer>
        <Content>
          {referral && (
            <>
              {selectedTab.value === 'summary' && (
                <RenderingErrorBoundary>
                  <SummaryTab review={referral} pendingActions={pendingActions.current} />
                </RenderingErrorBoundary>
              )}
              {selectedTab.value === 'files' && (
                <FilesTab
                  review={referral}
                  isSummarized={isSummarized}
                  files={referral.attachments ?? []}
                  onSummarizeFiles={handleSummarizeFromFilesTab}
                />
              )}
            </>
          )}
        </Content>
      </Container>
    </Foldout>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const StyledArrowIcon = styled(ArrowIcon)`
  margin-top: 6px;
  cursor: pointer;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PositionedClose = styled(CloseButton)`
  position: absolute;
  right: 24px;
  top: 16px;
`;

const FilterFoldoutHeader = styled.div`
  display: flex;
  flex-direction: column;

  padding: 40px 40px 0;
`;

const TabsContainer = styled.div`
  border-bottom: 1px solid ${colors.black10};
  padding: 24px 40px 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  flex: 1;
  padding: 24px 40px 40px;
  overflow-y: auto;
`;
